import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, ArrowLeft, ArrowRight, Check, Phone, Loader2, PartyPopper } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useQuote } from "@/context/QuoteContext";
import { SITE } from "@/data/site";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = ["Roofing", "Home Renovation", "Commercial Construction", "Roof Repair / Leak", "Financing / Payment Plan", "Not Sure / Other"];
const PROPERTY = ["Residential Home", "HOA / Multi-Family", "Commercial / Casino", "Other"];
const TIMELINES = ["Emergency, ASAP", "Within 1 month", "1–3 months", "Just researching"];

const ROOF_QS = [
  { key: "leaking", q: "Is your roof currently leaking?", opts: ["Yes", "No", "Not sure"] },
  { key: "age", q: "How old is your roof?", opts: ["Under 10 years", "10–20 years", "20+ years", "Not sure"] },
  { key: "goal", q: "What are you looking for?", opts: ["Repair", "Full replacement", "New install", "Inspection"] },
];

const Choice = ({ label, selected, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`w-full text-left rounded-xl border-2 px-4 py-3.5 font-semibold transition-all ${
      selected
        ? "border-red-700 bg-red-50 text-red-800"
        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
    }`}
  >
    <span className="flex items-center justify-between">
      {label}
      {selected && <Check className="h-5 w-5 text-red-700" />}
    </span>
  </button>
);

export const QuoteModal = () => {
  const { open, setOpen, presetService } = useQuote();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [data, setData] = useState({
    service: "", property_type: "", timeline: "", answers: {},
    name: "", email: "", phone: "", address: "", details: "",
  });

  useEffect(() => {
    if (open) {
      setStep(0);
      setDone(false);
      setData({
        service: presetService || "", property_type: "", timeline: "", answers: {},
        name: "", email: "", phone: "", address: "", details: "",
      });
    }
  }, [open, presetService]);

  const isRoof = data.service === "Roofing" || data.service === "Roof Repair / Leak";
  const steps = ["Service", "Property", isRoof ? "Roof Details" : "Timeline", "Contact"];
  const totalSteps = steps.length;

  const canNext = () => {
    if (step === 0) return !!data.service;
    if (step === 1) return !!data.property_type;
    if (step === 2) return isRoof ? true : !!data.timeline;
    return true;
  };

  const next = () => setStep((s) => Math.min(s + 1, totalSteps - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!data.name || !data.phone || !data.email) {
      toast.error("Please fill in your name, phone and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: data.name,
        email: data.email,
        phone: data.phone,
        service: data.service,
        property_type: data.property_type,
        timeline: data.timeline,
        address: data.address,
        details: data.details,
        answers: data.answers,
        source: "quote-modal",
      });
      setDone(true);
      toast.success("Quote request sent! We'll reach out shortly.");
    } catch (e) {
      toast.error("Something went wrong. Please call us at " + SITE.phone);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="quote-modal" className="sm:max-w-lg p-0 gap-0 overflow-hidden max-h-[92vh] overflow-y-auto">
        <DialogTitle className="sr-only">Get Your Free Quote from Templar Armor</DialogTitle>
        <DialogDescription className="sr-only">Interactive multi-step form to request a free, no-obligation roofing or construction quote.</DialogDescription>
        <div className="bg-slate-950 px-6 py-5 flex items-center gap-3">
          <div className="grid place-items-center h-9 w-9 rounded-lg bg-red-700 text-white">
            <Shield className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-display font-bold text-white text-lg leading-tight">Get Your Free Quote</h3>
            <p className="text-xs text-slate-400">No obligation, Owner-verified, Since 1980</p>
          </div>
        </div>

        {!done && (
          <div className="px-6 pt-4">
            <div className="flex items-center gap-1.5">
              {steps.map((_, i) => (
                <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-red-700" : "bg-slate-200"}`} />
              ))}
            </div>
            <p className="mt-2 text-xs font-semibold text-slate-500">Step {step + 1} of {totalSteps}, {steps[step]}</p>
          </div>
        )}

        <div className="px-6 py-5">
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div key="done" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8" data-testid="quote-success">
                <div className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-red-100 text-red-700 mb-4">
                  <PartyPopper className="h-8 w-8" />
                </div>
                <h3 className="font-display font-extrabold text-2xl text-slate-900">Request Received!</h3>
                <p className="mt-2 text-slate-600">Thank you, {data.name.split(" ")[0] || "friend"}. Our team will reach out shortly, often the same day.</p>
                <p className="mt-1 text-slate-600">Need us now?</p>
                <a href={SITE.phoneHref}>
                  <Button className="mt-4 bg-red-700 hover:bg-red-800 text-white font-bold rounded-full h-12 px-7">
                    <Phone className="mr-1 h-4 w-4" /> Call {SITE.phone}
                  </Button>
                </a>
                <button onClick={() => setOpen(false)} className="block mx-auto mt-4 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="quote-close-btn">Close</button>
              </motion.div>
            ) : (
              <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                {step === 0 && (
                  <div className="space-y-2.5">
                    <p className="font-display font-bold text-lg text-slate-900 mb-1">What do you need help with?</p>
                    {SERVICES.map((s) => (
                      <Choice key={s} label={s} selected={data.service === s} onClick={() => setData((d) => ({ ...d, service: s }))} testid={`qs-service-${s.replace(/\W+/g, "-").toLowerCase()}`} />
                    ))}
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-2.5">
                    <p className="font-display font-bold text-lg text-slate-900 mb-1">What type of property is it?</p>
                    {PROPERTY.map((p) => (
                      <Choice key={p} label={p} selected={data.property_type === p} onClick={() => setData((d) => ({ ...d, property_type: p }))} testid={`qs-property-${p.replace(/\W+/g, "-").toLowerCase()}`} />
                    ))}
                  </div>
                )}

                {step === 2 && isRoof && (
                  <div className="space-y-5">
                    {ROOF_QS.map((rq) => (
                      <div key={rq.key}>
                        <p className="font-display font-bold text-slate-900 mb-2">{rq.q}</p>
                        <div className="grid grid-cols-2 gap-2">
                          {rq.opts.map((o) => (
                            <Choice key={o} label={o} selected={data.answers[rq.key] === o} onClick={() => setData((d) => ({ ...d, answers: { ...d.answers, [rq.key]: o } }))} testid={`qs-${rq.key}-${o.replace(/\W+/g, "-").toLowerCase()}`} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {step === 2 && !isRoof && (
                  <div className="space-y-2.5">
                    <p className="font-display font-bold text-lg text-slate-900 mb-1">What's your timeline?</p>
                    {TIMELINES.map((t) => (
                      <Choice key={t} label={t} selected={data.timeline === t} onClick={() => setData((d) => ({ ...d, timeline: t }))} testid={`qs-timeline-${t.replace(/\W+/g, "-").toLowerCase()}`} />
                    ))}
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-4">
                    <p className="font-display font-bold text-lg text-slate-900">Where should we send your quote?</p>
                    <div>
                      <Label htmlFor="q-name" className="font-semibold">Full Name *</Label>
                      <Input id="q-name" data-testid="qs-name" value={data.name} onChange={(e) => setData((d) => ({ ...d, name: e.target.value }))} placeholder="John Smith" className="mt-1.5 h-11" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="q-phone" className="font-semibold">Phone *</Label>
                        <Input id="q-phone" data-testid="qs-phone" value={data.phone} onChange={(e) => setData((d) => ({ ...d, phone: e.target.value }))} placeholder="(555) 123-4567" className="mt-1.5 h-11" />
                      </div>
                      <div>
                        <Label htmlFor="q-email" className="font-semibold">Email *</Label>
                        <Input id="q-email" data-testid="qs-email" type="email" value={data.email} onChange={(e) => setData((d) => ({ ...d, email: e.target.value }))} placeholder="you@email.com" className="mt-1.5 h-11" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="q-address" className="font-semibold">Project Address / City</Label>
                      <Input id="q-address" data-testid="qs-address" value={data.address} onChange={(e) => setData((d) => ({ ...d, address: e.target.value }))} placeholder="City, CA" className="mt-1.5 h-11" />
                    </div>
                    <div>
                      <Label htmlFor="q-details" className="font-semibold">Anything else we should know?</Label>
                      <Textarea id="q-details" data-testid="qs-details" value={data.details} onChange={(e) => setData((d) => ({ ...d, details: e.target.value }))} placeholder="Tell us a bit about your project..." className="mt-1.5" rows={3} />
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {!done && (
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
            {step > 0 ? (
              <Button variant="ghost" onClick={back} data-testid="quote-back-btn" className="text-slate-600 font-semibold">
                <ArrowLeft className="mr-1 h-4 w-4" /> Back
              </Button>
            ) : <span />}
            {step < totalSteps - 1 ? (
              <Button onClick={next} disabled={!canNext()} data-testid="quote-next-btn" className="bg-red-700 hover:bg-red-800 text-white font-bold rounded-full px-7 h-11 disabled:opacity-50">
                Continue <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={submitting} data-testid="quote-submit-btn" className="bg-red-700 hover:bg-red-800 text-white font-bold rounded-full px-7 h-11">
                {submitting ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Sending...</> : <>Get My Free Quote <ArrowRight className="ml-1 h-4 w-4" /></>}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
